"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useJobPolling = void 0;
const react_1 = require("react");
const jobStatusApi_1 = require("../../../services/jobStatusApi");
const useJobPolling = (jobId, open, options) => {
    const [jobStatus, setJobStatus] = (0, react_1.useState)(null);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [isPolling, setIsPolling] = (0, react_1.useState)(false);
    const jobStatusApi = (0, jobStatusApi_1.useJobStatusApi)();
    const pollTimeoutRef = (0, react_1.useRef)(null);
    const hasTriggeredCompletion = (0, react_1.useRef)(false);
    const fetchInProgress = (0, react_1.useRef)(false);
    const isPollingRef = (0, react_1.useRef)(false);
    const optionsRef = (0, react_1.useRef)(options);
    optionsRef.current = options;
    const fetchJobStatus = (0, react_1.useCallback)(async () => {
        if (!jobId || fetchInProgress.current)
            return null;
        try {
            fetchInProgress.current = true;
            setIsLoading(true);
            setError(null);
            const jobStatusResponse = await jobStatusApi.getJobStatus(jobId);
            setJobStatus(jobStatusResponse);
            if (jobStatusResponse.status === 'completed' && !hasTriggeredCompletion.current) {
                hasTriggeredCompletion.current = true;
                setIsPolling(false);
                isPollingRef.current = false;
                optionsRef.current.onComplete?.(jobStatusResponse);
                if (optionsRef.current.autoClose) {
                    setTimeout(() => optionsRef.current.onClose?.(), optionsRef.current.autoCloseDelay);
                }
            }
            if (jobStatusResponse.status === 'failed' && !hasTriggeredCompletion.current) {
                hasTriggeredCompletion.current = true;
                setIsPolling(false);
                isPollingRef.current = false;
                const errorMessage = jobStatusResponse.result?.errors?.join(', ') || 'Operation failed';
                optionsRef.current.onError?.(errorMessage);
                if (optionsRef.current.autoClose) {
                    setTimeout(() => optionsRef.current.onClose?.(), optionsRef.current.autoCloseDelay + 3000);
                }
            }
            return jobStatusResponse;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch job status';
            const is403Error = errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('unauthorized');
            if (is403Error) {
                setIsPolling(false);
                isPollingRef.current = false;
                hasTriggeredCompletion.current = true;
                optionsRef.current.onError?.(errorMessage);
                setTimeout(() => optionsRef.current.onClose?.(), 2000);
                return null;
            }
            setError(errorMessage);
            setIsPolling(false);
            isPollingRef.current = false;
            optionsRef.current.onError?.(errorMessage);
            return null;
        }
        finally {
            setIsLoading(false);
            fetchInProgress.current = false;
        }
    }, [jobId, jobStatusApi]);
    const startPolling = (0, react_1.useCallback)(() => {
        if (!jobId || !open || isPollingRef.current)
            return;
        setIsPolling(true);
        isPollingRef.current = true;
        hasTriggeredCompletion.current = false;
        const poll = async () => {
            if (!isPollingRef.current)
                return;
            const result = await fetchJobStatus();
            if (result?.status === 'completed' || result?.status === 'failed') {
                setIsPolling(false);
                isPollingRef.current = false;
                return;
            }
            if (isPollingRef.current) {
                pollTimeoutRef.current = setTimeout(poll, optionsRef.current.pollInterval);
            }
        };
        poll();
    }, [jobId, open, fetchJobStatus]);
    const stopPolling = (0, react_1.useCallback)(() => {
        setIsPolling(false);
        isPollingRef.current = false;
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    }, []);
    (0, react_1.useEffect)(() => {
        if (open && jobId) {
            startPolling();
        }
        else {
            stopPolling();
        }
        return () => {
            stopPolling();
            hasTriggeredCompletion.current = false;
        };
    }, [open, jobId, startPolling, stopPolling]);
    return {
        jobStatus,
        isLoading,
        error,
        isPolling,
        stopPolling,
    };
};
exports.useJobPolling = useJobPolling;
